import { Component } from "@grafikr/components";
import { relewiseRecommend } from "utils/relewise";
import clear from "utils/clear";

export default Component((node, { onMounted, app }) => {
  const { img, vendor, handle, currentVariantId, crossStoreProductId, currentVariantSku } = node.dataset;

  const scrollToSTL = document.querySelector('[data-target="#shop-the-look"]');

  const noResultsCallback = () => {
    if (scrollToSTL) {
      scrollToSTL.remove();
    }

    node.closest(".shopify-section").remove();
  };

  function parseShopTheLook(string: string) {
    let url = string.replace("files/", "").split("_")[0];
    return url.split(/-(?=\d{9})/).map((id) => id.match(/\d{9}/)?.[0]);
  }

  // Create a debounced version of sendRequest to prevent multiple calls
  let requestInProgress = false;
  let requestDebounceTimer: number | null = null;

  const debouncedSendRequest = (sku, variantId) => {
    // If a request is in progress or debounce timer is active, don't send a new one
    if (requestInProgress) {
      return;
    }

    // Clear any pending debounce timer
    if (requestDebounceTimer) {
      clearTimeout(requestDebounceTimer);
    }

    // Set a small debounce to handle rapid-fire events
    requestDebounceTimer = window.setTimeout(() => {
      requestInProgress = true;

      sendRequest(sku, variantId).finally(() => {
        requestInProgress = false;
        requestDebounceTimer = null;
      });
    }, 50);
  };

  const sendRequest = async (sku, variantId) => {
    let json = {
      recommendations: [],
    };

    if (parseShopTheLook(img)[0]) {
      const response = await relewiseRecommend("CompleteTheLook", {
        crossStoreProductId: crossStoreProductId,
        variantId: sku,
        vendor: vendor,
        variantNames: parseShopTheLook(img),
      });

      json = await response.json();
    }

    if (json.recommendations.length > 0) {
      sessionStorage.setItem(
        `shop-the-look-${sku}`,
        json.recommendations.map((recommendation) => recommendation.productId).join(",")
      );

      const productHandles = [
        `${handle}?variant=${variantId}`,
        ...json.recommendations
          .filter(
            (entry) =>
              entry.productId &&
              entry.variant &&
              entry.variant.data[`${window.mosmosh.shop_code.toUpperCase()}_ShopifyHandle`]
          )
          .map((entry) => {
            return entry.variant.data[`${window.mosmosh.shop_code.toUpperCase()}_ShopifyHandle`].value;
          }),
      ];

      const productFetches = productHandles.map((handle) => {
        const url = new URL(`${window.location.origin}/products/${handle}`);
        url.searchParams.set("view", "stl");

        return fetch(url.toString());
      });
      const productItems = await Promise.all(
        (
          await Promise.all(productFetches)
        ).map((fetch) => {
          if (fetch.status === 200) {
            return fetch.text();
          }
        })
      );

      productItems.sort((a, b) => {
        const aData = a.match(/data-product-type="(\d+)"/);
        const bData = b.match(/data-product-type="(\d+)"/);

        if (aData && bData) {
          return parseInt(aData[1]) - parseInt(bData[1]);
        }

        return 0;
      });

      if (productItems.length > 1) {
        clear(node);
        node.insertAdjacentHTML(
          "beforeend",
          productItems
            .filter((item) => item)
            .map((html) => `<div class="w-full ${productItems.length > 2 ? "lg:w-1/4" : "lg:w-1/2"}">${html}</div>`)
            .join("")
        );
        app.mount();
      } else {
        noResultsCallback();
      }
    } else {
      noResultsCallback();
    }
  };

  onMounted(() => {
    let previousVariantId = currentVariantId;

    // Set up the event listener for future variant changes
    document.addEventListener("variant:changed", ({ detail }: CustomEvent) => {
      const { variant } = detail;

      if (previousVariantId !== variant.id) {
        previousVariantId = variant.id;
        debouncedSendRequest(variant.sku, variant.id);
      }
    });

    // Send initial request only once on mount
    if (currentVariantSku && currentVariantId) {
      debouncedSendRequest(currentVariantSku, currentVariantId);
    } else {
      noResultsCallback();
    }
  });
});
